import { Injectable } from '@angular/core';
import { createStore, StoreApi } from 'zustand/vanilla';
import { devtools, persist } from 'zustand/middleware';
import { AuthSlice, AuthStoreService } from '../auth/+store/auth-store.service';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ProvisionRequestsSlice,
  ProvisionRequestsStoreService,
} from '../provisions/+store/provision-requests-store.service';
import {
  LockerStoreService,
  LockerStoreSlice,
} from '../lockers/+store/locker-store.service';
import {
  PersonsSlice,
  PersonsStoreService,
} from '../persons/+store/persons-store.service';
import {
  ClothingStoreService,
  ClothingStoreSlice,
} from '../clothing/+store/clothing-store.service';
import {
  TableStateStoreService,
  TableStateStoreSlice,
} from '../shared/services/table-state/table-state.service';

export type State = AuthSlice &
  ProvisionRequestsSlice &
  LockerStoreSlice &
  PersonsSlice &
  ClothingStoreSlice &
  TableStateStoreSlice;

@Injectable()
export class StoreService {
  private readonly store: StoreApi<State>;

  constructor(
    private readonly authStore: AuthStoreService,
    private readonly provisionRequestsStore: ProvisionRequestsStoreService,
    private readonly lockerStore: LockerStoreService,
    private readonly personStore: PersonsStoreService,
    private readonly clothingStore: ClothingStoreService,
    private readonly tableStateStore: TableStateStoreService,
  ) {
    this.store = createStore<State>()(
      devtools(
        persist(
          (...operators) => ({
            ...authStore.getSlice()(...operators),
            ...provisionRequestsStore.getSlice()(...operators),
            ...lockerStore.getSlice()(...operators),
            ...personStore.getSlice()(...operators),
            ...clothingStore.getSlice()(...operators),
            ...tableStateStore.getSlice()(...operators),
          }),
          {
            name: 'store',
            partialize: (state) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { persons, provisionRequests, ...rest } = state;
              return rest;
            },
          },
        ),
      ),
    );
  }

  getStore() {
    return this.store;
  }

  getActions() {
    return {
      auth: this.authStore.getActions()(this.store),
      provisionRequests: this.provisionRequestsStore.getActions()(this.store),
      lockers: this.lockerStore.getActions()(this.store),
      persons: this.personStore.getActions()(this.store),
      clothing: this.clothingStore.getActions()(this.store),
      tables: this.tableStateStore.getActions()(this.store),
    };
  }

  select<StateValue>(
    selector: (state: State) => StateValue,
  ): Observable<StateValue> {
    const subject = new BehaviorSubject(selector(this.store.getState()));
    this.store.subscribe((state) => {
      const stateValue = selector(state);
      subject.next(stateValue);
    });
    return subject.asObservable();
  }
}
